/**
 * @file CategoryForm.tsx
 *
 * The category create/edit form — a faithful replica of the Website Builder v2
 * CategoriesTab form on this stack (react-intl, a custom controlled-state hook,
 * Gravity styling). Built on the shared {@link FormColumnLayout} primitives
 * (heading-above-card sections, `gv-label` fields, native `gv-input-select` /
 * `gv-input-textarea`), matching the product form. Owns the editable state via
 * {@link useCategoryFormState}, validates on save, and reports the assembled
 * {@link ShopCategoryPayload} to its parent. The parent-category select is built
 * from {@link buildCategoryTree}, which excludes the category itself and its
 * descendants to prevent cycles. Purely a view+state component: loading the seed
 * and persisting are the routed screen's job.
 */

import { useId, useMemo, useState } from 'react';
import type { RefObject } from 'react';
import { Link } from 'react-router-dom';
import { GeneralField, Icon } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import type { ShopCategory, ShopCategoryPayload } from '../../api/shop-types';
import { FormFooter } from '../../components/FormFooter';
import { ImageOrDefault } from '../../components/ImageOrDefault';
import { MediaDropZone } from '../../components/MediaDropZone';
import { MediaLibraryModal } from '../../components/MediaLibraryModal';
import {
  FormColumnLayout,
  FormSection,
  FormSectionHeading,
  FormSectionCard,
} from '../../components/form-layout/FormColumnLayout';
import { MAX_MEDIA_FILE_SIZE_MB } from '../../products/form/product-form-constants';
import { buildCategoryTree } from '../build-category-tree';
import { useCategoryFormState } from './use-category-form-state';
import { useCategoryFormImage } from './use-category-form-image';
import { buildCategoryPayload } from './category-form-utils';
import styles from './CategoryForm.module.css';

/** Which flow the form is serving; drives the title and save label. */
export type CategoryFormMode = 'create' | 'edit';

interface CategoryFormProps {
  category?: ShopCategory;
  mode: CategoryFormMode;
  categories: ShopCategory[];
  saving: boolean;
  saveError?: string;
  /** Machine-readable code of the last server failure, mapped to a field error. */
  serverErrorCode?: string | null;
  /** Clears the server error (called when the user edits the offending field). */
  onClearServerError: () => void;
  onSubmit: (payload: ShopCategoryPayload) => void;
  onCancel: () => void;
}

/**
 * Renders the category form.
 *
 * @param props - Seed category, mode, the full category list, save/server-error state, and handlers.
 *
 * @returns The category form.
 */
export function CategoryForm({
  category,
  mode,
  categories,
  saving,
  saveError,
  serverErrorCode,
  onClearServerError,
  onSubmit,
  onCancel,
}: CategoryFormProps) {
  const intl = useIntl();
  const nameId = useId();
  const slugId = useId();
  const parentId = useId();
  const descriptionId = useId();
  const [libraryOpen, setLibraryOpen] = useState(false);

  // The category itself and its descendants can't be its parent (would form a cycle).
  const forbiddenParentIds = useMemo(() => {
    if (!category) {
      return new Set<string>();
    }
    const descendants = buildCategoryTree(categories, undefined, category.id).map((n) => n.id);
    return new Set<string>([category.id, ...descendants]);
  }, [categories, category]);

  const { fields, patch, errors, validate, firstErrorRef } = useCategoryFormState(
    category,
    forbiddenParentIds,
  );

  const { uploading, imageError, handleFiles, handleError, selectFromLibrary, removeImage } =
    useCategoryFormImage(patch);

  const parentOptions = useMemo(
    () => [
      { value: '', label: intl.formatMessage(adminMessages.categoriesFormParentNone) },
      ...buildCategoryTree(categories, category?.id).map((node) => ({
        value: node.id,
        label: `${'   '.repeat(node.depth)}${node.depth > 0 ? '└ ' : ''}${node.name}`,
      })),
    ],
    [categories, category, intl],
  );

  // A slug conflict from the server maps to the slug field (the engine derives the
  // slug from the name when it's left blank, so editing either field clears it).
  const slugError =
    serverErrorCode === 'category_slug_conflict'
      ? intl.formatMessage(adminMessages.categoriesSlugConflict)
      : undefined;

  const handleSave = (): void => {
    if (!validate()) {
      return;
    }
    onSubmit(buildCategoryPayload(fields));
  };

  const title =
    mode === 'edit' ? adminMessages.categoriesFormEditTitle : adminMessages.categoriesFormAddTitle;
  const saveLabel =
    mode === 'edit' ? adminMessages.categoriesFormSave : adminMessages.categoriesFormCreate;
  const hasImage = fields.imageSrc !== '';

  return (
    <div className={styles.form}>
      <header className="gv-flex gv-flex-col gv-w-full gv-gap-sm">
        <div className="gv-breadcrumbs">
          <Link to="/categories">
            <Icon name="arrow_back" size="small" />
            <FormattedMessage {...adminMessages.formBack} />
          </Link>
        </div>
        <h2 className="gv-heading-lg gv-m-0">
          <FormattedMessage {...title} />
        </h2>
      </header>

      <FormColumnLayout>
        <FormSection>
          <FormSectionHeading
            title={intl.formatMessage(adminMessages.categoriesFormInformation)}
          />
          <FormSectionCard>
            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={nameId}>
                <span>
                  <FormattedMessage {...adminMessages.categoriesFormName} />
                </span>
              </label>
              <GeneralField
                id={nameId}
                inputRef={firstErrorRef as RefObject<HTMLInputElement>}
                value={fields.name}
                data-testid="category-form-name"
                placeholder={intl.formatMessage(adminMessages.categoriesFormNamePlaceholder)}
                error={errors.nameError}
                aria-label={intl.formatMessage(adminMessages.categoriesFormName)}
                onChange={(event) => {
                  patch({ name: event.target.value });
                  onClearServerError();
                }}
              />
            </div>

            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={slugId}>
                <span>
                  <FormattedMessage {...adminMessages.categoriesFormSlug} />
                </span>
                <span className="gv-label-optional">
                  <FormattedMessage {...adminMessages.formOptional} />
                </span>
              </label>
              <GeneralField
                id={slugId}
                value={fields.slug}
                data-testid="category-form-slug"
                error={slugError}
                aria-label={intl.formatMessage(adminMessages.categoriesFormSlug)}
                onChange={(event) => {
                  patch({ slug: event.target.value });
                  onClearServerError();
                }}
              />
              <p className="gv-caption-lg gv-m-0">
                <FormattedMessage {...adminMessages.categoriesFormSlugHint} />
              </p>
            </div>

            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={parentId}>
                <span>
                  <FormattedMessage {...adminMessages.categoriesFormParent} />
                </span>
              </label>
              <div className="gv-input gv-input-select gv-w-full">
                <select
                  id={parentId}
                  value={fields.parent}
                  data-testid="category-form-parent"
                  aria-label={intl.formatMessage(adminMessages.categoriesFormParent)}
                  onChange={(event) => patch({ parent: event.target.value })}
                >
                  {parentOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <Icon name="expand_more" size="small" />
              </div>
              {errors.parentError && (
                <span className="gv-text-sm gv-state-critical" role="alert">
                  {errors.parentError}
                </span>
              )}
            </div>

            <div className="gv-form-option gv-w-full">
              <label className="gv-label" htmlFor={descriptionId}>
                <span>
                  <FormattedMessage {...adminMessages.categoriesFormDescription} />
                </span>
                <span className="gv-label-optional">
                  <FormattedMessage {...adminMessages.formOptional} />
                </span>
              </label>
              <textarea
                id={descriptionId}
                className="gv-input gv-input-textarea gv-w-full"
                value={fields.description}
                rows={4}
                data-testid="category-form-description"
                aria-label={intl.formatMessage(adminMessages.categoriesFormDescription)}
                onChange={(event) => patch({ description: event.target.value })}
              />
            </div>
          </FormSectionCard>
        </FormSection>

        <FormSection>
          <FormSectionHeading title={intl.formatMessage(adminMessages.formMedia)} showOptional />
          <FormSectionCard>
            {hasImage ? (
              <div className="gv-flex gv-flex-col gv-items-start gv-gap-sm">
                <ImageOrDefault
                  src={fields.imageSrc}
                  alt={fields.name}
                  className="gv-thumbnail-image"
                />
                <button
                  type="button"
                  className="gv-button gv-button-cancel gv-mode-condensed"
                  data-testid="category-form-image-remove"
                  onClick={removeImage}
                >
                  <Icon name="delete" size="small" />
                  <FormattedMessage {...adminMessages.formRemoveImage} />
                </button>
              </div>
            ) : (
              <MediaDropZone
                testId="category-form-image"
                onFilesSelected={handleFiles}
                onError={handleError}
                maxFileSizeMb={MAX_MEDIA_FILE_SIZE_MB}
                remainingSlots={uploading ? 0 : 1}
                disabled={uploading}
                title={intl.formatMessage(adminMessages.formMediaDropTitle)}
                browseLabel={intl.formatMessage(adminMessages.formUploadFromComputer)}
                hint={intl.formatMessage(adminMessages.formMediaHint, {
                  maxMb: MAX_MEDIA_FILE_SIZE_MB,
                })}
                onSelectFromLibrary={() => setLibraryOpen(true)}
                libraryLabel={intl.formatMessage(adminMessages.formSelectFromLibrary)}
                orLabel={intl.formatMessage(adminMessages.formOr)}
              />
            )}
            {uploading && (
              <span className="gv-text-sm gv-text-on-alternative">
                <FormattedMessage {...adminMessages.formUploading} />
              </span>
            )}
            {imageError && (
              <span className="gv-text-sm gv-state-critical" role="alert">
                {imageError}
              </span>
            )}
          </FormSectionCard>
        </FormSection>

        {saveError && (
          <p
            className="gv-text-sm gv-state-critical"
            role="alert"
            data-testid="category-form-error"
          >
            {saveError}
          </p>
        )}

        <FormFooter>
          <button
            type="button"
            className="gv-button gv-button-cancel"
            data-testid="category-form-cancel"
            onClick={onCancel}
            disabled={saving}
          >
            <FormattedMessage {...adminMessages.formBack} />
          </button>
          <button
            type="button"
            className="gv-button gv-button-primary"
            data-testid="category-form-save"
            onClick={handleSave}
            disabled={saving || uploading}
          >
            {saving ? (
              <FormattedMessage {...adminMessages.categoriesFormSaving} />
            ) : (
              <FormattedMessage {...saveLabel} />
            )}
          </button>
        </FormFooter>
      </FormColumnLayout>

      <MediaLibraryModal
        isOpen={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        allowMultiSelect={false}
        onAdd={(items) => {
          const image = items.find((item) => item.mediaType === 'image');
          if (image) {
            selectFromLibrary({ url: image.url, id: image.id });
          }
        }}
      />
    </div>
  );
}
